import type { FolderItem } from '../types/folder.types'
import { getApiTreeChildren } from './normalizeApiTree'

type RawFolderItem = Omit<FolderItem, 'children'> & {
  children?: RawFolderItem[]
  childrens?: RawFolderItem[]
}

/** Приводит дерево папок из API к единому полю children */
export function normalizeFolderItem(item: RawFolderItem): FolderItem {
  const rawChildren = getApiTreeChildren(item) as RawFolderItem[]

  return {
    id: item.id,
    name: item.name,
    count_documents: item.count_documents,
    expanded: item.expanded,
    children: rawChildren.map(normalizeFolderItem),
  }
}

export function normalizeFolderTree(items: RawFolderItem[]): FolderItem[] {
  return items.map(normalizeFolderItem)
}

function isGlobalLibraryNode(item: FolderItem): boolean {
  const id = String(item.id).trim().toLowerCase()
  if (id === 'global' || id.startsWith('global_')) {
    return true
  }

  const name = item.name.trim().toLowerCase()

  return name.includes('глобал') || name.includes('global')
}

export function excludeGlobalFolders(items: FolderItem[]): FolderItem[] {
  return items
    .filter(item => !isGlobalLibraryNode(item))
    .map(item => ({
      ...item,
      children: excludeGlobalFolders(item.children ?? []),
    }))
}

/** Возвращает ID предков папки (для раскрытия пути к targetId) */
export function getPathToFolder(
  items: FolderItem[],
  targetId: FolderItem['id'],
  preferredRootId?: FolderItem['id'] | null,
): Array<FolderItem['id']> {
  const target = String(targetId)

  const find = (
    list: FolderItem[],
    parents: Array<FolderItem['id']>,
  ): Array<FolderItem['id']> | null => {
    for (const item of list) {
      if (String(item.id) === target) {
        return parents
      }
      if (item.children?.length) {
        const found = find(item.children, [...parents, item.id])
        if (found) {
          return found
        }
      }
    }

    return null
  }

  if (preferredRootId != null && String(preferredRootId) !== '') {
    const preferred = String(preferredRootId)
    const preferredRoot = items.find(item => String(item.id) === preferred)
    if (preferredRoot) {
      if (String(preferredRoot.id) === target) {
        return []
      }
      const inPreferred = find(preferredRoot.children ?? [], [preferredRoot.id])
      if (inPreferred) {
        return inPreferred
      }
    }

    return []
  }

  for (const item of items) {
    if (String(item.id) === target) {
      return []
    }
  }

  return find(items, []) ?? []
}

export function isFolderInSubtree(root: FolderItem, targetId: FolderItem['id']): boolean {
  const target = String(targetId)
  if (String(root.id) === target) {
    return true
  }

  const walk = (items: FolderItem[]): boolean => {
    for (const item of items) {
      if (String(item.id) === target) {
        return true
      }
      if (item.children?.length && walk(item.children)) {
        return true
      }
    }

    return false
  }

  return walk(root.children ?? [])
}

export function collectExpandedIds(items: FolderItem[]): Set<string> {
  const ids = new Set<string>()

  const walk = (list: FolderItem[]) => {
    for (const item of list) {
      if (item.expanded) {
        ids.add(String(item.id))
      }
      if (item.children?.length) {
        walk(item.children)
      }
    }
  }

  walk(items)

  return ids
}

/** Добавляет в набор предков и саму папку — чужие ветки не трогает */
export function addPathToExpandedIds(
  items: FolderItem[],
  folderId: FolderItem['id'],
  expandedIds: Set<string>,
  preferredRootId?: FolderItem['id'] | null,
): void {
  if (folderId == null || folderId === '') {
    return
  }

  getPathToFolder(items, folderId, preferredRootId)
    .forEach(id => expandedIds.add(String(id)))
  expandedIds.add(String(folderId))
}
